import { ArrowUp, X } from 'lucide-react';
import { Fragment, useId, useState } from 'react';

import { IntercomMessengerIcon } from '@apps/web/components/shared/intercom_messenger_icon';
import { Button } from '@apps/web/components/ui/button';
import { useDraggableLauncherPosition } from '@apps/web/hooks/use_draggable_launcher_position';
import { cn } from '@apps/web/lib/utils';

// Local-only preview of the support messenger. No Intercom SDK calls are made.
export function IntercomMockup({
    bottomPaddingPx,
}: {
    readonly bottomPaddingPx: number;
}) {
    const drag = useDraggableLauncherPosition();
    const [open, setOpen] = useState(false);
    const panelId = useId();
    const bottom = `max(${bottomPaddingPx}px, var(--safe-bottom, 0px))`;

    return (
        <Fragment>
            {open && (
                <section
                    id={panelId}
                    data-testid="intercom-mockup-panel"
                    role="dialog"
                    aria-label="Support chat preview"
                    className="bg-background fixed z-[2147483000] flex h-[min(540px,calc(100dvh-160px))] w-[min(380px,calc(100vw-40px))] flex-col overflow-hidden rounded-2xl border shadow-2xl"
                    style={{
                        left: 'var(--intercom-left, 20px)',
                        bottom: `calc(${bottom} + 64px)`,
                    }}
                >
                    <header className="bg-[#5533ee] px-5 pt-5 pb-7 text-white">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold">
                                    Phoebe support
                                </h2>
                                <p className="mt-1 text-sm text-white/80">
                                    Intercom messenger mockup
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-8 text-white hover:bg-white/15 hover:text-white"
                                aria-label="Close support preview"
                                onClick={() => setOpen(false)}
                            >
                                <X className="size-4" />
                            </Button>
                        </div>
                    </header>
                    <div className="bg-muted/30 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
                        <p className="text-muted-foreground text-center text-xs">
                            Example conversation
                        </p>
                        <div className="bg-background max-w-[85%] rounded-2xl rounded-bl-sm border px-4 py-3 text-sm leading-5 shadow-sm">
                            Hi there! How can we help you today?
                        </div>
                        <div className="bg-primary text-primary-foreground ml-auto max-w-[85%] rounded-2xl rounded-br-sm px-4 py-3 text-sm leading-5">
                            I have a question about my Inbox.
                        </div>
                        <div className="bg-background max-w-[85%] rounded-2xl rounded-bl-sm border px-4 py-3 text-sm leading-5 shadow-sm">
                            We can help with that. Tell us what you’re trying to
                            do.
                        </div>
                    </div>
                    <footer className="border-t p-4">
                        <p className="text-muted-foreground mb-2 text-center text-xs">
                            Preview only · Messages are not sent
                        </p>
                        <div className="text-muted-foreground flex items-center justify-between rounded-xl border px-3 py-2 text-sm">
                            <span>Write a message…</span>
                            <ArrowUp aria-hidden="true" className="size-4" />
                        </div>
                    </footer>
                </section>
            )}
            <Button
                type="button"
                data-testid="intercom-mockup-launcher"
                aria-label={
                    open ? 'Close support preview' : 'Open support preview'
                }
                aria-controls={panelId}
                aria-expanded={open}
                title="Support preview · Drag to move"
                className={cn(
                    'fixed z-[2147482999] size-12 touch-none rounded-full bg-white p-0 text-neutral-950 shadow-lg transition-none select-none hover:bg-white dark:bg-white dark:text-neutral-950 dark:hover:bg-white',
                    drag.isDragging ? 'cursor-grabbing' : 'cursor-grab',
                )}
                style={
                    drag.position === null
                        ? {
                              left: 'var(--intercom-left, 20px)',
                              top: 'auto',
                              bottom,
                          }
                        : {
                              left: drag.position.left,
                              top: drag.position.top,
                              bottom: 'auto',
                          }
                }
                onPointerDown={drag.onPointerDown}
                onPointerMove={drag.onPointerMove}
                onPointerUp={drag.onPointerUp}
                onPointerCancel={drag.onPointerCancel}
                onClick={() => {
                    if (!drag.consumeDragClick()) {
                        setOpen((previous) => !previous);
                    }
                }}
            >
                {open ? (
                    <X aria-hidden="true" className="size-6" />
                ) : (
                    <IntercomMessengerIcon />
                )}
            </Button>
        </Fragment>
    );
}
